import type { SearchItem } from '../../../app/router/search'
import type { ClassSummary, StudentRow } from '../../../shared/api/classes'
import type { SchoolStaffRow } from '../../../shared/api/staff'

/**
 * The school tier's own records, shaped for the chrome's search box.
 *
 * 🔴 WHY THIS IS ALLOWED TO EXIST WHEN `search.ts` ARGUED IT SHOULD NOT.
 * That file refused a client-side search over school data, and its reasoning was
 * right: *"a client-side search over whatever a screen happened to have fetched
 * would answer 'no results' for a child who exists, which is a worse answer than
 * no search box."* The defect there is the word **happened**. A box fed by
 * whatever screen you were last on is unreliable by construction.
 *
 * This is fed by three calls that each return a school's COMPLETE collection —
 * `getAllStudents`, `getClasses`, `getSchoolStaff`. None takes a filter, none
 * paginates, and the Children screen already pages its rows client-side, which
 * is only possible because it holds all of them. So the index sees everything
 * the server would have, and the false negative the objection was about cannot
 * happen. The rule this keeps is the one that matters: **never index a partial
 * list.** If any of these three ever grows a `limit`, this must move to the
 * server the same day, or it starts lying.
 *
 * ⚠️ IT IS A SCHOOL'S OWN DATA AND NOTHING ELSE. Scope is the server's — every
 * one of these three endpoints is already scoped to the caller's school by
 * `ScopeService`, so this adds no reach. It cannot: it only re-indexes rows the
 * browser was already handed.
 *
 * ⚠️ NO PARENT PHONE NUMBERS, DELIBERATELY. `StudentRow.parentPhone` arrives
 * masked and the parent's NAME is already here, so indexing a masked number
 * would add a field that matches on digits nobody can read back. A search box is
 * a surface that reads out what it matched; the less of a child's file it can
 * be made to echo, the better.
 *
 * 🔴 PURE, LIKE THE RANKING IT FEEDS (OD-60 — no DOM renderer in this repo).
 */

/** Where a record hit lands: the screen that holds it, with its filter seeded. */
function seeded(path: string, q: string): string {
  return `#/${path}?q=${encodeURIComponent(q)}`
}

/**
 * ⚠️ THE CHILD'S OWN NAME IS THE SEED, NOT THE QUERY THAT FOUND THEM. Somebody
 * who typed "zoe" and picked Zoë Fernandes should land on a Children screen
 * filtered to that child — seeding the screen with "zoe" would work by accident
 * here and fail the moment the hit came from a roll number or a parent's name.
 */
export function schoolSearchItems(
  students: readonly StudentRow[],
  classes: readonly ClassSummary[],
  staff: readonly SchoolStaffRow[],
): SearchItem[] {
  const rows: SearchItem[] = []

  for (const s of students) {
    const fields: SearchItem['fields'] = [
      /* The roll number is the second thing anyone knows a child by, and often
         the first thing a teacher has written down. */
      ...(s.rollNumber ? [{ text: s.rollNumber, weight: 0.9 }] : []),
      { text: s.className, weight: 0.6 },
      ...(s.grade ? [{ text: s.grade, weight: 0.6 }] : []),
      ...(s.parentName ? [{ text: s.parentName, weight: 0.5 }] : []),
    ]
    rows.push({
      id: `child:${s.playerId}`,
      label: s.name,
      /* The hint is what tells two children of the same name apart, which is why
         it is the class and the roll rather than the word "Child". */
      hint: s.rollNumber ? `${s.className} · Roll ${s.rollNumber}` : s.className,
      kind: 'child',
      fields,
      href: seeded('school/students', s.name),
    })
  }

  for (const c of classes) {
    const parts = [c.grade, c.section].filter((p): p is string => Boolean(p))
    rows.push({
      id: `class:${c.id}`,
      label: c.name,
      hint: parts.length > 0 ? parts.join(' · ') : 'Class',
      kind: 'class',
      /* "Class 2 B" as one string so "class 2 b" matches it token by token even
         when the name itself is "Butterflies". */
      fields: parts.length > 0 ? [{ text: parts.join(' '), weight: 0.8 }] : [],
      href: seeded('school/classes', c.name),
    })
  }

  for (const t of staff) {
    /*
     * ⚠️ A STAFF ROW WITH NO NAME IS SKIPPED, NOT LABELLED WITH ITS EMAIL. An
     * invited teacher who has not claimed their account yet has `name: null`,
     * and a result row reading "sunil@kalqy.in" is a credential on screen for a
     * person who is not usefully findable anyway.
     */
    if (!t.name) continue
    rows.push({
      id: `teacher:${t.userId}`,
      label: t.name,
      hint: t.isActive ? 'Teacher' : 'Teacher · inactive',
      kind: 'teacher',
      fields: t.email ? [{ text: t.email, weight: 0.7 }] : [],
      href: seeded('school/teachers', t.name),
    })
  }

  return rows
}
